// ─────────────────────────────────────────────────────────────────────────────
// EQ SCORING ENGINE
// Input:  answers[] — integers 1-5 for each of 96 questions
// Output: { scales, subscales, profile }
// ─────────────────────────────────────────────────────────────────────────────
import { QUESTIONS } from "./questions.js";
import { DOMAINS } from "./factors.js";

export function scoreEQ(answers) {
  // Step 1: raw scores per domain and subscale
  const rawDomain    = {};
  const cntDomain    = {};
  const rawSubscale  = {};
  const cntSubscale  = {};

  DOMAINS.forEach(d => { rawDomain[d.id]=0; cntDomain[d.id]=0; });

  QUESTIONS.forEach((q, i) => {
    const raw = answers[i];
    if (!raw || raw < 1 || raw > 5) return;
    const scored = q.keyed === -1 ? (6 - raw) : raw;

    rawDomain[q.domain]  = (rawDomain[q.domain]  || 0) + scored;
    cntDomain[q.domain]  = (cntDomain[q.domain]  || 0) + 1;

    const key = q.sub;
    rawSubscale[key] = (rawSubscale[key] || 0) + scored;
    cntSubscale[key] = (cntSubscale[key] || 0) + 1;
  });

  // Step 2: convert to 1-10 scale
  const scales = {};
  DOMAINS.forEach(d => {
    const cnt = cntDomain[d.id] || 1;
    const avg = rawDomain[d.id] / cnt;         // 1.0 – 5.0
    scales[d.id] = Math.max(1, Math.min(10, Math.round((avg - 1) / 4 * 9) + 1));
  });

  const subscales = {};
  Object.entries(rawSubscale).forEach(([sub, sum]) => {
    const cnt = cntSubscale[sub] || 1;
    const avg = sum / cnt;
    subscales[sub] = Math.max(1, Math.min(10, Math.round((avg - 1) / 4 * 9) + 1));
  });

  // Step 3: overall EQ composite (weighted average)
  const weights = { SA:0.25, SR:0.25, MO:0.20, EM:0.20, SS:0.10 };
  const overall  = +Object.entries(weights)
    .reduce((sum, [d, w]) => sum + (scales[d] || 5) * w, 0)
    .toFixed(1);

  // Step 4: 9-box axes
  // X = Self (SA + SR) / 2
  // Y = Social (EM + SS) / 2
  const selfAxis    = +((scales.SA + scales.SR) / 2).toFixed(1);
  const socialAxis  = +((scales.EM + scales.SS) / 2).toFixed(1);
  const perfAvg     = selfAxis;
  const potAvg      = socialAxis;
  const perfBox     = perfAvg <= 4 ? 1 : perfAvg <= 7 ? 2 : 3;
  const potBox      = potAvg  <= 4 ? 1 : potAvg  <= 7 ? 2 : 3;

  return {
    scales,      // { SA, SR, MO, EM, SS } → 1-10
    subscales,   // { "Emotional Awareness", ... } → 1-10
    overall,     // composite EQ score 1-10
    validityFlag: null,
    perfAvg, potAvg, perfBox, potBox,
    axes: { x: selfAxis, y: socialAxis },
  };
}
